import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios"
import { API_V1 } from "@/lib/constants"
import { CSRF_HEADER, csrfToken } from "@/lib/csrf"
import { useAuthStore } from "@/store/auth.store"

/** RFC 7807 Problem Details response shape from FastAPI backend */
export interface ProblemDetail {
  type: string
  title: string
  status: number
  detail: string
  instance: string
  errors?: Array<{ field: string; message: string; type: string }>
}

const apiClient: AxiosInstance = axios.create({
  baseURL: API_V1,
  headers: { "Content-Type": "application/json" },
  timeout: 30_000,
  // Sends the HttpOnly session cookie (same-origin by default; see lib/constants)
  withCredentials: true,
})

const SAFE = new Set(["get", "head", "options"])

// ── Request: CSRF token on state-changing requests ────────────────────────────

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!SAFE.has((config.method ?? "get").toLowerCase())) {
    const token = csrfToken()
    if (token && config.headers) config.headers[CSRF_HEADER] = token
  }
  return config
})

// ── Response: a 401 means the session ended (expired, revoked, signed out) ────

/** Where to send the browser when the session is gone; the reason shows on the login page. */
export function goToLogin(reason: "expired" | "signed-out" = "expired"): void {
  useAuthStore.getState().clear()
  if (window.location.pathname.startsWith("/auth/")) return
  const from = window.location.pathname + window.location.search
  window.location.replace(`/auth/login?reason=${reason}&from=${encodeURIComponent(from)}`)
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ProblemDetail>) => {
    const url = error.config?.url ?? ""
    // Login failures and the initial "am I signed in?" probe are handled by their callers
    if (error.response?.status === 401 && !url.startsWith("/auth/login") && !url.startsWith("/auth/me")) {
      goToLogin("expired")
    }
    // An administrator reset the password while this page was open: show the change-password screen
    if (error.response?.status === 403 && error.response.data?.title === "Password Change Required") {
      const { me, setMe } = useAuthStore.getState()
      if (me && !me.account.must_change_password) setMe({ ...me, account: { ...me.account, must_change_password: true } })
    }
    return Promise.reject(error)
  },
)

export default apiClient
